import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable, { TableStyles } from "react-data-table-component";
import "./ActivityLog.css";
import { AiOutlineInfoCircle } from "react-icons/ai";
import { GrNotes } from "react-icons/gr";
import { toZoneDateTime } from "../components/utils/DateUtils";
import Select from "react-select";
import flatpickr from "flatpickr";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import type { Instance as FlatpickrInstance } from "flatpickr/dist/types/instance";
import { CiSearch } from "react-icons/ci";

import { SingleValue } from "react-select";
import { AiOutlineSend } from "react-icons/ai";

import moment from "moment-timezone";
import { useCheckSessionHeartBeat } from "./API/ApiEnpoints";

type SeverityOption = {
  value: string;
  label: string;
};

interface Activity {
  datetime: string;
  severity: string;
  description: string;
  type: string;
  sub_type: string;
  log_data: any;
  info: {
    user_uid: string;
    org_uid: string;
    site_uid?: string;
  };
}

type SiteOption = {
  label: string;
  value: string;
  site_uid: string;
};

type Filters = {
  from_datetime: string;
  to_datetime: string;
  severity?: string;
  org_uid?: string;
  site_uid?: string;
};
interface OrgInfo {
  org_uid: string;
  active_user_count: number;
  inactive_user_count: number;
  admins: string[];
}

interface OrgResponse {
  type: string;
  status: string;
  message: string;
  data: OrgInfo[];
}

interface OrgOption {
  label: string;
  value: string;
}

const ActivityLog: React.FC = () => {
  useCheckSessionHeartBeat();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalData, setModalData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  // const [modalType, setModalType] = useState(null);
  // const [shouldFetchLogs, setShouldFetchLogs] = useState(true);
  const timezone = localStorage.getItem("timeZone") ?? "UTC";

  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([]);
  //const orgOptions = [{ label: "EM", value: "em" }];
  // const [filters, setFilters] = useState<Filters>({
  //   from_datetime: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 mins ago
  //   to_datetime: new Date().toISOString(), // current time
  //   severity: "",
  // });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const todayStart = moment()
    .tz(timezone)
    .startOf("day")
    .format("YYYY-MM-DDTHH:mm");
  const todayEnd = moment()
    .tz(timezone)
    .endOf("day")
    .format("YYYY-MM-DDTHH:mm");

  const [filters, setFilters] = useState<{
    from_datetime?: string;
    to_datetime?: string;
    severity?: string;
    org_uid?: string;
    site_uid?: string;
  }>({
    from_datetime: todayStart,
    to_datetime: todayEnd,
  });

  const [searchQuery, setSearchQuery] = useState<string>("");

  const [originalFilters, setOriginalFilters] = useState({
    from_datetime: moment()
      .tz(timezone)
      .subtract(30, "minutes")
      .format("YYYY-MM-DDTHH:mm"),
    to_datetime: moment().tz(timezone).format("YYYY-MM-DDTHH:mm"),
  });
  const [shouldFetchLogs, setShouldFetchLogs] = useState(false);

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  const fetchActivityLog = async (
    page: number,
    pageSize: number,
    searchQuery: string,
  ) => {
    setLoading(true);
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value),
      );

      // Construct params object conditionally
      const params: any = {
        page,
        page_size: pageSize,
        ...cleanedFilters,
      };

      // Only add search query if it's not empty
      if (searchQuery.trim() !== "") {
        // Check if the search query is a valid date
        const parsedDate = new Date(searchQuery);
        if (!isNaN(parsedDate.getTime())) {
          // Check if it's a valid date
          // If valid, convert to UTC ISO string
          params.search = parsedDate.toISOString(); // Convert to UTC
        } else {
          // If not a valid date, just use the search query as is
          params.search = searchQuery;
        }
      }

      const response = await axios.get(`${beAddress}/api/system-activity`, {
        headers: {
          "user-uid": userUid,
        },
        withCredentials: true,
        params,
      });

      const data = response.data.data;
      setActivities(data.activity_records);
      setTotalRows(data.total_count);
    } catch (error) {
      console.error("Failed to fetch activity log:", error);
    } finally {
      setLoading(false);
    }
  };
  const fetchOrg = async () => {
    setLoading(true);
    try {
      const response = await axios.get<OrgResponse>(
        `${beAddress}/api/org-info`,
        {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        },
      );

      const orgData = response.data.data;

      const formattedOptions: OrgOption[] = orgData.map((org) => ({
        label: org.org_uid.toUpperCase(),
        value: org.org_uid,
      }));

      setOrgOptions(formattedOptions);
    } catch (error) {
      console.error("Failed to fetch org info:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivityLog(currentPage, perPage, searchQuery);
    fetchOrg();
  }, [currentPage, perPage, filters]);

  const handlePageChange = (page: number) => setCurrentPage(page);
  useEffect(() => {
    const { from_datetime, to_datetime } = filters;

    if (from_datetime && to_datetime) {
      const from = new Date(from_datetime);
      const to = new Date(to_datetime);

      if (from >= to) {
        setErrorMessage("From datetime must be earlier than To datetime.");
        setShouldFetchLogs(false);
      } else {
        setErrorMessage("");
        setShouldFetchLogs(true);
      }
    }
  }, [filters.from_datetime, filters.to_datetime]);

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const handleOpenModal = (logData: any, type: string) => {
    setModalData(logData);
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleFilterChange = (name: string, value: string) => {
    let updatedFilters = { ...filters, [name]: value };

    setFilters(updatedFilters);
  };
  const handleDateChange = (
    selectedDates: Date[],
    dateStr: string,
    instance: FlatpickrInstance,
  ) => {
    const name = (instance.element as HTMLInputElement).name;

    if (!selectedDates.length) {
      setFilters((prev) => ({
        ...prev,
        [name]: "",
      }));
      return;
    }

    const isoDate = selectedDates[0].toISOString();

    setFilters((prev) => ({
      ...prev,
      [name]: isoDate,
    }));
  };

  const generatePerPageOptions = () => {
    const options = new Set<number>();
    options.add(perPage);
    if (totalRows > 0) options.add(totalRows);
    [5, 10, 20, 50, 100, 250, 500, 1000, 1500].forEach((num) => {
      if (num <= totalRows) {
        options.add(num);
      }
    });
    return Array.from(options).sort((a, b) => a - b);
  };

  const tableCustomStyles: TableStyles = {
    table: {
      style: {
        backgroundColor: "var(--card-background)",
        color: "var(--text-color)",
      },
    },
    headRow: {
      style: {
        backgroundColor: "var(--card-background)",
        color: "var(--text-color)",
        borderBottomColor: "var(--text-color)",
        borderBottomWidth: "1px",
        borderBottomStyle: "solid" as const, // Fixes TS error
      },
    },
    headCells: {
      style: {
        color: "var(--text-color)",
        fontSize: "14px",
      },
    },
    rows: {
      style: {
        backgroundColor: "var(--card-background)",
        color: "var(--text-color)",
        "&:not(:last-of-type)": {
          borderBottomStyle: "solid" as const, // Fixes TS error
          borderBottomWidth: "1px",
          borderBottomColor: "var(--text-color)",
        },
      },
      highlightOnHoverStyle: {
        backgroundColor: "rgba(255, 255, 255, 0.1)",
        color: "var(--text-color)",
        transitionDuration: "0.15s",
        transitionProperty: "background-color",
        borderBottomColor: "var(--text-color)",
        outline: "none",
      },
    },
    pagination: {
      style: {
        backgroundColor: "var(--card-background)",
        color: "var(--text-color)",
        borderTop: "1px solid var(--text-color)",
      },
      pageButtonsStyle: {
        color: "var(--text-color)",
        fill: "var(--text-color)",
        "&:disabled": {
          color: "var(--text-color)",
          fill: "var(--text-color)",
          opacity: 0.4,
        },
        "&:hover:not(:disabled)": {
          backgroundColor: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
  };

  const customPerPageOptions = generatePerPageOptions();
  const columns = [
    // {
    //   name: "User UID",
    //   selector: (row: Activity) => row.info.user_uid,
    //   sortable: true,
    //   wrap: true,
    // },
    {
      name: "Date & Time",
      selector: (row: Activity) => toZoneDateTime(row.datetime),
      sortable: true,
    },
    {
      name: "Type",
      selector: (row: Activity) => row.type,
    },
    {
      name: "Sub-type",
      selector: (row: Activity) => row.sub_type,
    },
    {
      name: "Severity",
      selector: (row: Activity) => row.severity,
    },
    {
      name: "Description",
      selector: (row: Activity) => row.description,
      wrap: true,
    },
    {
      name: "Info",
      cell: (row: Activity) => (
        <button
          className="activitylog-info"
          onClick={() => handleOpenModal(row.info, "info")}
        >
          <AiOutlineInfoCircle className="info-log-data" />
        </button>
      ),
    },
    {
      name: "Log Data",
      cell: (row: Activity) => (
        <button
          className="activitylog-log"
          onClick={() => handleOpenModal(row.log_data, "log")}
        >
          <GrNotes className="info-log-data" />
        </button>
      ),
    },
  ];

  const severityOptions = [
    { value: "INFO", label: "INFO" },
    { value: "CRITICAL", label: "CRITICAL" },
    { value: "ERROR", label: "ERROR" },
  ];
  // const handleDateChange = (selectedDates, dateStr, instance) => {
  //   const name = instance.element.name; // Get the name from Flatpickr's input element
  //   let updatedFilters = { ...filters };

  //   if (name === "from_datetime" || name === "to_datetime") {
  //     if (!selectedDates.length) {
  //       updatedFilters[name] = null;
  //     } else {
  //       const utcValue = selectedDates[0];
  //       updatedFilters[name] = utcValue;
  //     }

  //     if (updatedFilters.from_datetime && updatedFilters.to_datetime) {
  //       if (
  //         moment(updatedFilters.from_datetime).isAfter(
  //           updatedFilters.to_datetime
  //         )
  //       ) {
  //         setErrorMessage("From datetime must be earlier than To datetime.");
  //         setShouldFetchLogs(false);
  //       } else {
  //         setErrorMessage("");
  //         setShouldFetchLogs(true);
  //       }
  //     }
  //   } else {
  //     updatedFilters[name] = dateStr;
  //   }
  //   setFilters(updatedFilters);
  // };
  const onInputReady = (input: HTMLInputElement) => {
    input.readOnly = true;
  };
  const handleSearch = () => {
    if (searchQuery.trim() !== "") {
      setCurrentPage(1); // Reset to the first page on new search
      fetchActivityLog(1, perPage, searchQuery); // Fetch logs with the current search query
    }
  };
  const clearInputOnFocus = (instance: any) => {
    instance.clear();
  };
  const renderObject = (obj: any, parentKey = "") => {
    return Object.entries(obj).map(([key, value]) => {
      const newKey = parentKey ? `${parentKey}.${key}` : key;
      if (typeof value === "object" && value !== null) {
        return (
          <div key={newKey}>
            <strong>{newKey}:</strong>
            <div style={{ paddingLeft: "20px" }}>
              {renderObject(value, newKey)}
            </div>
          </div>
        );
      }
      return (
        <div key={newKey}>
          <strong>{newKey}:</strong> {String(value)}
        </div>
      );
    });
  };
  return (
    <div className="activitylog-container">
      <h3 className="userdevicemaps-userlist">Activity Logs</h3>
      {/*<div className="userdevicemaps-line" />*/}
      <div className="activitylog-topcontainer">
        <label>
          Organization UID:
          <Select
            name="org_uid"
            // --- START OF FIX ---
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: "var(--card-background)",
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: "var(--card-background)",
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused
                  ? "lightgray"
                  : "var(--card-background)",
                color: "inherit",
              }),
            }}
            // --- END OF FIX ---
            value={
              orgOptions.find((option) => option.value === filters.org_uid) ||
              null
            }
            onChange={(selected) =>
              handleFilterChange("org_uid", selected ? selected.value : "")
            }
            options={orgOptions}
            placeholder="Select organization"
            isClearable
          />
        </label>

        <label>
          From DateTime:
          <Flatpickr
            className="flatpickr-datetime-input"
            name="from_datetime"
            value={
              filters.from_datetime
                ? new Date(filters.from_datetime)
                : undefined
            }
            onChange={handleDateChange}
            options={{
              enableTime: true,
              dateFormat: "Y-m-d H:i",
              time_24hr: true,
              minuteIncrement: 1,
              allowInput: true,
              disableMobile: true,
              position: "auto center",
              maxDate: moment().tz(timezone).format("YYYY-MM-DD HH:mm"),
            }}
            onReady={(selectedDates, dateStr, instance) => {
              onInputReady(instance.input);
            }}
          />
        </label>

        <label>
          To DateTime:
          <Flatpickr
            className="flatpickr-datetime-input"
            name="to_datetime"
            value={
              filters.to_datetime ? new Date(filters.to_datetime) : undefined
            }
            onChange={handleDateChange}
            options={{
              enableTime: true,
              dateFormat: "Y-m-d H:i",
              time_24hr: true,
              minuteIncrement: 1,
              allowInput: true,
              disableMobile: true,
              position: "auto center",
              minDate: moment(filters.from_datetime).format("YYYY-MM-DD HH:mm"),
              maxDate: moment().tz(timezone).format("YYYY-MM-DD HH:mm"),
            }}
            onReady={(selectedDates, dateStr, instance) => {
              onInputReady(instance.input);
            }}
          />
        </label>
        {/*<button
          type="button"
          onClick={() => {
            setFilters((prev) => {
              const { from_datetime, to_datetime, ...rest } = prev;
              return rest;
            });
          }}
          style={{
            padding: "6px 12px",
            background: "var(--card-background)",
            color: "var(--text-color)",
            border: "1px solid #ccc",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        >
          Show All Logs
        </button>*/}

        <label>
          Severity:
          <Select
            name="severity"
            // --- START OF FIX ---
            styles={{
              control: (base) => ({
                ...base,
                backgroundColor: "var(--card-background)",
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: "var(--card-background)",
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isFocused
                  ? "lightgray"
                  : "var(--card-background)",
                color: "inherit",
              }),
            }}
            // --- END OF FIX ---
            value={
              filters.severity
                ? severityOptions.find(
                    (option) => option.value === filters.severity,
                  ) || null
                : null
            }
            onChange={(selectedOption) =>
              setFilters((prevFilters) => ({
                ...prevFilters,
                severity: selectedOption ? selectedOption.value : "",
              }))
            }
            options={severityOptions}
            placeholder="Select Severity"
            isClearable
          />
        </label>
      </div>
      <div className="activity-search-container">
        <div className="activity-search-box">
          <CiSearch className="activity-search-icon" />

          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => {
              const newSearchQuery = e.target.value;
              setSearchQuery(newSearchQuery);
              // Call fetchActivityLog whenever the search query changes
              setCurrentPage(1); // Reset to the first page on new search
              fetchActivityLog(1, perPage, newSearchQuery); // Fetch logs with the current search query
            }}
            className="activity-search-bar"
          />
          {/*<AiOutlineSend
            className="activity-search-icon"
            onClick={() => {
              // Optionally, you can also call fetchActivityLog here if you want to trigger it on button click
              fetchActivityLog(currentPage, perPage, searchQuery);
            }}
          />*/}
        </div>
      </div>

      <DataTable
        columns={columns}
        data={activities}
        progressPending={loading}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        paginationRowsPerPageOptions={customPerPageOptions}
        customStyles={tableCustomStyles}
      />

      {isModalOpen && (
        <div className="activitylog-modal-overlay">
          <div className="activitylog-modal-content">
            <button
              className="activitylog-close-button"
              onClick={handleCloseModal}
            >
              &times;
            </button>

            {modalType === "info" && (
              <>
                <h2>Info Details</h2>
                {modalData ? (
                  <>
                    <p>
                      <strong>user_uid:</strong> {modalData.user_uid}
                    </p>
                    <h5>Action taken in:</h5>
                    <p>
                      <strong>org_uid:</strong> {modalData.org_uid}
                    </p>
                  </>
                ) : (
                  <p>No details available.</p>
                )}
              </>
            )}
            {modalType === "log" && (
              <>
                <h2>Log Data</h2>
                {modalData ? (
                  <>
                    {typeof modalData === "object" ? (
                      renderObject(modalData)
                    ) : (
                      <p>{modalData}</p>
                    )}
                  </>
                ) : (
                  <p>No details available.</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityLog;
